<template>
  <div id="app" class="flex flex-col min-h-screen bg-white">
    <!-- Header: Fixed at top, remounts on login/logout (hidden on dashboard routes or when explicitly hidden) -->
    <SiteHeader v-if="shouldShowHeader" :key="headerKey" @logged-out="onLoggedOut" />

    <main class="flex-grow">
      <router-view />
    </main>

    <!-- Footer: Only rendered when content is ready, sticks to bottom via flexbox -->
    <SiteFooter v-if="shouldShowFooter" class="mt-auto" />

    <!-- Mobile bottom nav on Home, Quiz and Community pages (component hides on md+ screens) -->
    <MobileBottomNav
      v-if="shouldShowFooter && $route && (
        $route.path === '/' ||
        $route.path.startsWith('/quiz') ||
        $route.path.startsWith('/community') ||
          $route.path.startsWith('/learn')
      )"
    />

    <!-- Global Modals - Only render when needed -->
    <LoginModal 
      v-if="loginModal"
      v-model:show="loginModal" 
      :message="loginMessage" 
      @switch-to-trial="switchToTrial" 
      @switch-to-forgot="switchToForgotPassword" 
      @login-success="onLoginSuccess" 
    />

    <ForgotPasswordModal 
      v-if="forgotPasswordModal"
      v-model:show="forgotPasswordModal" 
      @switch-to-login="switchToLoginFromForgot" 
      @reset-success="switchToLogin" 
    />

    <TrialModal 
      v-if="trialModal"
      v-model:show="trialModal" 
      @switch-to-login="switchToLogin" 
    />

    <ShareModal 
      v-if="ShareModal"
      v-model:show="ShareModal" 
      :quiz="shareQuizData" 
    />

    <AddRemarkModal 
      v-if="addRemarkModal"
      v-model:show="addRemarkModal" 
      @remark-added="onRemarkAdded" 
    />

    <GlobalResourceModal 
      v-if="showGlobalResourceModal"
      @play-video="onGlobalResourcePlay" 
    />

    <!-- Study Goal Manager Modal -->
    <div v-if="showStudyGoalManagerModal" class="fixed inset-0 z-[1100] flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div class="bg-white rounded-2xl w-full max-w-5xl h-[90vh] overflow-hidden">
        <div class="flex items-center justify-between p-4 border-b">
          <h3 class="text-lg font-semibold">Create Goal</h3>
          <button aria-label="Action" @click="showStudyGoalManagerModal = false" class="text-gray-600 p-2"><i class="fas fa-times"></i></button>
        </div>
        <div class="w-full h-full overflow-auto">
          <StudyGoalManager />
        </div>
      </div>
    </div>

    <!-- Global Connections Popup Modal -->
    <ConnectionsPopup
      v-if="showGlobalConnectionsPopup"
      :show="showGlobalConnectionsPopup"
      :user-id="globalConnectionsUserId"
      :statistics="globalConnectionsStatistics"
      :initial-tab="globalConnectionsTab"
      @update:show="showGlobalConnectionsPopup = $event"
    />

    <!-- Global Toaster - Only load when toast is shown -->
    <Toaster v-if="showToaster" />
  </div>
</template>

<script>
import { initializeApp } from "firebase/app";
import { defineAsyncComponent } from 'vue';

import SiteFooter from "@/components/SiteFooter.vue";
import SiteHeader from "@/components/SiteHeader.vue";
import MobileBottomNav from "@/components/MobileBottomNav.vue";
import ConnectionsPopup from '@/components/ConnectionsPopup.vue';

// Lazy-load all modal and non-essential components to improve initial load time
const LoginModal = defineAsyncComponent(() => import('@/components/LoginModal.vue'));
const ForgotPasswordModal = defineAsyncComponent(() => import('@/components/ForgotPasswordModal.vue'));
const TrialModal = defineAsyncComponent(() => import('@/components/TrialModal.vue'));
const ShareModal = defineAsyncComponent(() => import('@/components/quiz/ShareModal.vue'));
const AddRemarkModal = defineAsyncComponent(() => import('@/components/AddRemarkModal.vue'));
const GlobalResourceModal = defineAsyncComponent(() => import('@/components/resources/GlobalResourceModal.vue'));
const StudyGoalManager = defineAsyncComponent(() => import('@/components/StudyGoalManager.vue'));
const Toaster = defineAsyncComponent(() => import('@/components/Toaster.vue'));

export default {
  name: "AppLayout",
  components: {
    SiteHeader,
    SiteFooter,
    LoginModal,
    ForgotPasswordModal,
    TrialModal,
    ShareModal,
    AddRemarkModal,
    GlobalResourceModal,
    StudyGoalManager,
    MobileBottomNav,
    Toaster,
    ConnectionsPopup,
  },



  data() {
    return {
      headerKey: 0,
      loginModal: false,
      forgotPasswordModal: false,
      trialModal: false,
      loginMessage: "",
      ShareModal: false,
      shareQuizData: {},
      hideFooterForQuiz: false,
      addRemarkModal: false,
      showStudyGoalManagerModal: false,
      showGlobalResourceModal: false,
      showToaster: false,
      // Reactive flag to centrally control visibility of the global SiteHeader
      isGlobalHeaderHidden: false,
      // Global Connections Popup State
      showGlobalConnectionsPopup: false,
      globalConnectionsUserId: null,
      globalConnectionsTab: 'followers',
      globalConnectionsStatistics: {},
    };
  },

  computed: {
    isDashboardRoute() {
      return this.$route && (this.$route.path.startsWith('/dashboard') || this.$route.path.startsWith('/control-panel'));
    },

    isControlPanelRoute() {
      return this.$route && this.$route.path.startsWith('/control-panel');
    },

    // Header is shown only when not on dashboard routes and when not explicitly hidden
    shouldShowHeader() {
      return !this.isDashboardRoute && !this.isGlobalHeaderHidden;
    },

    // Footer visibility: hide for quiz flows and for control-panel pages
    shouldShowFooter() {
      return !this.hideFooterForQuiz && !this.isControlPanelRoute;
    }
  },

  mounted() {
    // Listen for global connections popup open event
    window.addEventListener('open-connections-popup', (e) => {
      // Use global last profile info set by ProfilePage
      const userId = window.__lastProfileUserId || null
      const statistics = window.__lastProfileStatistics || {}
      this.globalConnectionsUserId = userId
      this.globalConnectionsStatistics = statistics
      this.globalConnectionsTab = (e.detail && e.detail.tab) ? e.detail.tab : 'followers'
      this.showGlobalConnectionsPopup = true
    })
    // 🔥 FOOTER TOGGLE LISTENER
    window.addEventListener("toggle-footer", (e) => {
      this.hideFooterForQuiz = e.detail.hide;
    });

    // Reset header visibility when routes change away from dashboard
    this.$watch(() => this.$route && this.$route.path, (newPath) => {
      if (!String(newPath || '').startsWith('/dashboard')) {
        // Ensure any explicit hide is cleared when not on dashboard
        this.isGlobalHeaderHidden = false;
      }
    });

    // 🔥 Initialize Firebase
    const firebaseConfig = {
      apiKey: "AIzaSyBjz58Li9wLrdaNeaDL8emDaZU_FRlKIXs",
      authDomain: "ost-test-e6f10.firebaseapp.com",
      projectId: "ost-test-e6f10",
      storageBucket: "ost-test-e6f10.firebasestorage.app",
      messagingSenderId: "844313185370",
      appId: "1:844313185370:web:c014b66a7827d676739c63",
      measurementId: "G-7VLEJ5WS5W",
    };

    // Initialize Firebase app only; push notification permission
    // must be requested after an explicit user action (see `EnableNotifications` component).
    initializeApp(firebaseConfig);

    // Listen for toast events to load Toaster component on demand
    this.setupToasterListeners();
  },

  created() {
    // 🔥 GLOBAL MODALS
    window.openLoginModal = (message = "") => {
      this.loginMessage = message;
      this.loginModal = true;
    };

    window.openForgotPasswordModal = () => {
      this.forgotPasswordModal = true;
    };

    window.openTrialModal = () => {
      this.trialModal = true;
    };

    window.openShareModal = (quiz) => {
      this.shareQuizData = quiz || {};
      this.ShareModal = true;
    };

    window.openAddRemarkModal = () => {
      this.addRemarkModal = true;
    };

    window.openStudyGoalsModal = () => {
      this.showStudyGoalManagerModal = true;
    };

    window.openGlobalResourceModal = () => {
      this.showGlobalResourceModal = true;
    };

    // 🔧 GLOBAL HEADER CONTROL (used by dashboard pages)
    // Provide functions early so pages can call them during created/mounted
    window.hideGlobalHeader = () => {
      try {
        this.isGlobalHeaderHidden = true;
        // also clear any inline display set by older code
        const hdr = document.querySelector('header, #app-header, .global-header');
        if (hdr) hdr.style.display = 'none';
      } catch (e) {
        // noop
      }
    };

    window.showGlobalHeader = () => {
      try {
        this.isGlobalHeaderHidden = false;
        const hdr = document.querySelector('header, #app-header, .global-header');
        if (hdr) hdr.style.display = '';
      } catch (e) {
        // noop
      }
    };

    // Also expose on $root for components using $root.hideGlobalHeader
    if (this.$root) {
      this.$root.hideGlobalHeader = window.hideGlobalHeader;
      this.$root.showGlobalHeader = window.showGlobalHeader;
    }
  },

  methods: {
    setupToasterListeners() {
      // Load toaster component when any toast method is called
      const originalToast = this.$toast;
      const originalShowToast = this.$showToast;

      // Ensure toaster is loaded before showing toasts
      const ensureToasterLoaded = () => {
        if (!this.showToaster) {
          this.showToaster = true;
        }
      };

      // Intercept toast calls if they exist
      if (originalToast) {
        ['success', 'error', 'warning', 'info'].forEach(method => {
          if (originalToast[method]) {
            const originalMethod = originalToast[method];
            originalToast[method] = (...args) => {
              ensureToasterLoaded();
              return originalMethod.apply(originalToast, args);
            };
          }
        });
      }

      if (originalShowToast) {
        ['success', 'error', 'warning', 'info'].forEach(method => {
          if (originalShowToast[method]) {
            const originalMethod = originalShowToast[method];
            originalShowToast[method] = (...args) => {
              ensureToasterLoaded();
              return originalMethod.apply(originalShowToast, args);
            };
          }
        });
      }
    },

    // Called by pages that want to hide the global header (backwards-compatible)
    hideGlobalHeader() {
      this.isGlobalHeaderHidden = true;
    },

    // Called by pages that want to show the global header (backwards-compatible)
    showGlobalHeader() {
      this.isGlobalHeaderHidden = false;
    },



    switchToTrial() {
      this.loginModal = false;
      this.forgotPasswordModal = false;
      this.loginMessage = "";
      this.trialModal = true;
    },

    switchToLogin() {
      this.trialModal = false;
      this.forgotPasswordModal = false;
      this.loginModal = true;
    },

    switchToForgotPassword() {
      this.loginMessage = "";
      this.loginModal = false;
      this.trialModal = false;
      this.forgotPasswordModal = true;
    },

    switchToLoginFromForgot() {
      this.forgotPasswordModal = false;
      this.loginModal = true;
    },

    onLoginSuccess() {
      this.loginMessage = "";
      this.loginModal = false;
      // Bump headerKey to remount SiteHeader and show logged-in state
      this.headerKey += 1;
    },

    onRemarkAdded(payload) {
      this.addRemarkModal = false;
      
      // Ensure toaster is loaded before showing toast
      this.showToaster = true;
      
      this.$nextTick(() => {
        if (this.$toast?.success) {
          this.$toast.success("Remark added");
        } else if (this.$showToast?.success) {
          this.$showToast.success("Remark added");
        } else {
          console.log("Remark added:", payload);
        }
      });
    },

    onGlobalResourcePlay(resource) {
      if (!resource) return;

      try {
        if (resource.file_path) {
          window.open(`/${resource.file_path}`, "_blank");
        } else if (resource.link) {
          window.open(resource.link, "_blank");
        }
      } catch (err) {
        console.error("Error handling global resource play:", err);
      }
    },

    onLoggedOut() {
      // Bump headerKey to remount SiteHeader and show logged-out state
      this.headerKey += 1;
    },
  },
};
</script>